import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BrokerService } from '../../../services/broker/broker.service';
import { ApiServiceService } from '../../../services/common/api-service.service';

@Component({
  selector: 'app-broker-login',
  templateUrl: './broker-login.component.html',
  styleUrls: ['./broker-login.component.scss']
})
export class BrokerLoginComponent implements OnInit, OnDestroy {
  isRegisterMode = false;
  
  // Form data
  name = '';
  agency_name = '';
  email = '';
  phone = '';
  password = '';
  city = '';
  office_address = '';
  years_of_experience: number | null = null;
  marriages_facilitated: number | null = null;
  upi_id = '';
  agreed_to_terms = false;
  
  base64ProfilePhoto = '';
  profilePhotoPreview = '';
  base64IdProof = '';
  idProofPreview = '';

  loading = false;
  error = '';
  returnUrl = '/broker/dashboard';

  // ── OTP State ────────────────────────────────────────────────────────────
  showOtpModal = false;
  otpStep: 'channel' | 'verify' = 'channel';
  otpChannel: 'sms' | 'whatsapp' = 'sms';
  otpDigits: string[] = ['', '', '', '', '', ''];
  otpError = '';
  otpSending = false;
  otpVerifying = false;
  resendSeconds = 0;
  private resendInterval: any = null;

  constructor(
    private brokerService: BrokerService,
    private apiService: ApiServiceService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    if (this.brokerService.isLoggedIn()) {
      this.router.navigate(['/broker/dashboard']);
    }
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/broker/dashboard';
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
  }

  toggleMode(): void {
    this.isRegisterMode = !this.isRegisterMode;
    this.error = '';
  }

  onPhotoSelected(event: any, isIdProof: boolean = false): void {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        if (isIdProof) {
          this.base64IdProof = reader.result as string;
          this.idProofPreview = reader.result as string;
        } else {
          this.base64ProfilePhoto = reader.result as string;
          this.profilePhotoPreview = reader.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  onSubmit(): void {
    this.error = '';
    if (!this.email || !this.password) {
      this.error = 'Please enter email and password.';
      return;
    }

    if (this.isRegisterMode) {
      if (!this.name || !this.phone || !this.city || !this.office_address) {
        this.error = 'Please fill in all required fields (Name, Email, Mobile, Password, City, Office Address).';
        return;
      }
      if (!this.agreed_to_terms) {
        this.error = 'You must agree to the Terms & Conditions.';
        return;
      }

      // Open OTP Modal before submitting registration!
      this.openOtpModal();
    } else {
      this.loading = true;
      this.brokerService.login(this.email, this.password).subscribe({
        next: (res) => {
          this.loading = false;
          if (res.status === 'success') {
            this.router.navigate([this.returnUrl]);
          } else {
            this.error = typeof res.data === 'string' ? res.data : (res.message || 'Login failed');
          }
        },
        error: (err) => {
          this.loading = false;
          this.error =
            (typeof err?.error?.data === 'string' && err.error.data) ||
            err?.error?.message ||
            (typeof err?.error === 'string' && err.error) ||
            err?.message ||
            'Invalid email or password';
        }
      });
    }
  }

  doBrokerRegister(): void {
    this.loading = true;
    this.brokerService.register({
      name: this.name,
      agency_name: this.agency_name,
      email: this.email,
      phone: this.phone,
      password: this.password,
      city: this.city,
      office_address: this.office_address,
      years_of_experience: this.years_of_experience || 0,
      marriages_facilitated: this.marriages_facilitated || 0,
      upi_id: this.upi_id,
      agreed_to_terms: this.agreed_to_terms,
      base64ProfilePhoto: this.base64ProfilePhoto,
      base64IdProof: this.base64IdProof
    }).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.status === 'success') {
          alert(res.message || 'Broker application submitted successfully! Your account is currently under manual review by Admin. Once approved, you can log in and upload profiles.');
          this.isRegisterMode = false; // Switch to login mode
          this.error = '';
        } else {
          this.error = res.message || 'Registration failed';
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err.error?.message || 'Error connecting to server';
      }
    });
  }

  // ── OTP Modal & Flow ──────────────────────────────────────────────────────
  openOtpModal() {
    this.otpStep = 'channel';
    this.otpDigits = ['', '', '', '', '', ''];
    this.otpError = '';
    this.showOtpModal = true;
  }

  closeOtpModal() {
    this.showOtpModal = false;
    this.clearResendTimer();
    this.otpDigits = ['', '', '', '', '', ''];
    this.otpError = '';
  }

  selectChannel(channel: 'sms' | 'whatsapp') {
    this.otpChannel = channel;
    this.otpError = '';
  }

  sendOtp() {
    const mobile = this.phone;
    this.otpSending = true;
    this.otpError = '';
    this.clearResendTimer();

    this.apiService.sendOtp(mobile, this.otpChannel).subscribe({
      next: (res) => {
        this.otpSending = false;
        if (res.status === 'success') {
          this.otpStep = 'verify';
          this.startResendTimer();
          setTimeout(() => {
            const first = document.getElementById('broker-otp-box-0') as HTMLInputElement;
            first?.focus();
          }, 100);
        } else {
          this.otpError = res.data || 'Failed to send OTP.';
        }
      },
      error: (err) => {
        this.otpSending = false;
        this.otpError = err.error?.data || 'Failed to send OTP. Please try again.';
      }
    });
  }

  resendOtp() {
    if (this.resendSeconds > 0) return;
    this.sendOtp();
  }

  switchChannelFromVerify() {
    this.otpChannel = this.otpChannel === 'sms' ? 'whatsapp' : 'sms';
    this.otpStep = 'channel';
    this.otpError = '';
  }

  trackByIndex(index: number): number {
    return index;
  }

  private lastInputTime = 0;
  private lastInputChar = '';
  private lastInputIndex = -1;

  onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const val = input.value.replace(/\D/g, '');

    if (val && val.length === 1 && index === this.lastInputIndex + 1 && val === this.lastInputChar && (Date.now() - this.lastInputTime) < 80) {
      input.value = '';
      return;
    }

    if (val.length > 1) {
      for (let j = 0; j < val.length && (index + j) < 6; j++) {
        this.otpDigits[index + j] = val[j];
        const box = document.getElementById(`broker-otp-box-${index + j}`) as HTMLInputElement;
        if (box) box.value = val[j];
      }
      this.otpDigits = [...this.otpDigits];
      const targetIndex = Math.min(index + val.length, 5);
      setTimeout(() => {
        const targetBox = document.getElementById(`broker-otp-box-${targetIndex}`) as HTMLInputElement;
        targetBox?.focus();
      }, 15);
      return;
    }

    const singleDigit = val ? val[val.length - 1] : '';
    this.otpDigits[index] = singleDigit;
    this.otpDigits = [...this.otpDigits];
    input.value = singleDigit;

    if (singleDigit) {
      this.lastInputIndex = index;
      this.lastInputChar = singleDigit;
      this.lastInputTime = Date.now();
    }

    if (singleDigit && index < 5) {
      setTimeout(() => {
        const next = document.getElementById(`broker-otp-box-${index + 1}`) as HTMLInputElement;
        if (next) {
          next.focus();
          next.select();
        }
      }, 15);
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace') {
      if (!this.otpDigits[index] && index > 0) {
        this.otpDigits[index - 1] = '';
        this.otpDigits = [...this.otpDigits];
        const prev = document.getElementById(`broker-otp-box-${index - 1}`) as HTMLInputElement;
        if (prev) {
          prev.value = '';
          prev.focus();
        }
      } else {
        this.otpDigits[index] = '';
        this.otpDigits = [...this.otpDigits];
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      const prev = document.getElementById(`broker-otp-box-${index - 1}`) as HTMLInputElement;
      prev?.focus();
    } else if (event.key === 'ArrowRight' && index < 5) {
      const next = document.getElementById(`broker-otp-box-${index + 1}`) as HTMLInputElement;
      next?.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = event.clipboardData?.getData('text').replace(/\D/g, '') || '';
    for (let i = 0; i < 6; i++) {
      this.otpDigits[i] = text[i] || '';
      const box = document.getElementById(`broker-otp-box-${i}`) as HTMLInputElement;
      if (box) box.value = this.otpDigits[i];
    }
    this.otpDigits = [...this.otpDigits];
    const lastIdx = Math.min(text.length - 1, 5);
    setTimeout(() => {
      const box = document.getElementById(`broker-otp-box-${lastIdx}`) as HTMLInputElement;
      box?.focus();
    }, 15);
  }

  get otpValue(): string {
    let digits = [...this.otpDigits];
    for (let i = 0; i < 6; i++) {
      if (!digits[i]) {
        const el = document.getElementById(`broker-otp-box-${i}`) as HTMLInputElement;
        if (el && el.value && el.value.trim().length === 1) {
          digits[i] = el.value.trim();
          this.otpDigits[i] = digits[i];
        }
      }
    }
    return digits.join('');
  }

  get otpComplete(): boolean {
    return this.otpValue.length === 6;
  }

  verifyOtp() {
    if (!this.otpComplete || this.otpVerifying) return;
    const mobile = this.phone;
    this.otpVerifying = true;
    this.otpError = '';

    this.apiService.verifyOtp(mobile, this.otpValue).subscribe({
      next: (res) => {
        this.otpVerifying = false;
        if (res.status === 'success') {
          this.closeOtpModal();
          this.doBrokerRegister();
        } else {
          this.otpError = res.data || 'Incorrect OTP.';
        }
      },
      error: (err) => {
        this.otpVerifying = false;
        this.otpError = err.error?.data || 'Incorrect OTP. Please try again.';
        this.otpDigits = ['', '', '', '', '', ''];
        setTimeout(() => {
          const first = document.getElementById('broker-otp-box-0') as HTMLInputElement;
          first?.focus();
        }, 100);
      }
    });
  }

  private startResendTimer() {
    this.resendSeconds = 30;
    this.clearResendTimer();
    this.resendInterval = setInterval(() => {
      this.resendSeconds--;
      if (this.resendSeconds <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
      this.resendInterval = null;
    }
  }
}
